import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Loader2, School, MapPin, Globe, FileText, CheckCircle, ChevronRight, ChevronLeft, ImageIcon, Map } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import apiService from '../services/api'
import profileService from '../services/profileService'
import mediaService from '../services/mediaService'
import toast from 'react-hot-toast'
import DocumentsRequiredModal from '../components/DocumentsRequiredModal'
import DocumentPreviewCard, { DocumentPreviewItem } from '../components/DocumentPreviewCard'
import HowToReachForm from '../components/HowToReachForm'
import MapLocationPicker from '../components/MapLocationPicker'
import CountrySelect from '../components/CountrySelect'
import { ACCEPT_STRINGS, ACCEPTED_MIME_TYPES, MEDIA_SECTIONS, validateDocumentFile, validateImageFile } from '../config/mediaValidation'
import { validateSchool, LIMITS } from '../validation'

type TransportMode = 'TRAIN' | 'BUS' | 'TAXI' | 'AIR'

interface SchoolForm {
  name: string
  slug: string
  description: string
  shortDescription: string
  location: string
  country: string
  city: string
  address: string
  phone: string
  email: string
  website: string
}

interface HowToReachOption {
  mode: TransportMode
  details: string
}

interface HowToReachStation {
  name: string
  options: HowToReachOption[]
}

const CreateSchool: React.FC = () => {
  const { user, schools, loading, refreshSchools, logout } = useAuth()
  const [formData, setFormData] = useState<SchoolForm>({
    name: '',
    slug: '',
    description: '',
    shortDescription: '',
    location: '',
    country: '',
    city: '',
    address: '',
    phone: '',
    email: '',
    website: '',
  })
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showDocumentsModal, setShowDocumentsModal] = useState(false)
  const [currentStep, setCurrentStep] = useState<1 | 2>(1)
  const [docData, setDocData] = useState({
    businessRegNumber: '',
    businessRegDoc: null as DocumentPreviewItem | null,
    gstNumber: '',
    gstDoc: null as DocumentPreviewItem | null,
    addressProofDoc: null as DocumentPreviewItem | null,
    yogaAllianceNumber: '',
    yogaAllianceDoc: null as DocumentPreviewItem | null,
  })
  const [otherDocuments, setOtherDocuments] = useState<DocumentPreviewItem[]>([])
  const [logoData, setLogoData] = useState<{ fileKey: string; url: string; uploading: boolean } | null>(null)
  const [howToReach, setHowToReach] = useState<HowToReachStation[]>([
    { name: '', options: [{ mode: 'TRAIN', details: '' }] },
  ])
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null)
  const [showMapPicker, setShowMapPicker] = useState(false)
  const navigatingToRegistrationRef = useRef(false)
  const navigate = useNavigate()

  // Redirect if not logged in
  useEffect(() => {
    if (!loading && !user && !navigatingToRegistrationRef.current) {
      navigate('/login', { replace: true })
    }
  }, [user, loading, navigate])

  // If user already has a school, redirect to dashboard
  useEffect(() => {
    if (!loading && schools && schools.length > 0) {
      navigate('/dashboard', { replace: true })
    }
  }, [schools, loading, navigate])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user && !navigatingToRegistrationRef.current) {
    return <Navigate to="/login" replace />
  }

  if (schools && schools.length > 0) {
    return <Navigate to="/dashboard" replace />
  }

  // Show message if user is not authenticated
  if (!user) {
    return null // Will redirect via useEffect
  }

  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
      .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value
    setFormData({
      ...formData,
      name,
      slug: generateSlug(name),
    })
    if (errors.name) {
      setErrors({ ...errors, name: '' })
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' })
    }
  }

  const validateForm = (): boolean => {
    const newErrors = validateSchool(formData)
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNextStep = () => {
    if (validateForm()) {
      setCurrentStep(2)
    }
  }

  const handleBackStep = () => {
    setCurrentStep(1)
    setErrors({})
  }

  const handleBackToRegistration = () => {
    const confirmed = window.confirm(
      'All your progress will be lost. Are you sure you want to go back to the registration page?'
    )
    if (!confirmed) return

    navigatingToRegistrationRef.current = true

    // Reset in-memory form state
    setFormData({
      name: '',
      slug: '',
      description: '',
      shortDescription: '',
      location: '',
      country: '',
      city: '',
      address: '',
      phone: '',
      email: '',
      website: '',
    })
    setDocData({
      businessRegNumber: '',
      businessRegDoc: null,
      gstNumber: '',
      gstDoc: null,
      addressProofDoc: null,
      yogaAllianceNumber: '',
      yogaAllianceDoc: null,
    })
    setOtherDocuments([])
    setLogoData(null)
    setHowToReach([{ name: '', options: [{ mode: 'TRAIN', details: '' }] }])
    setCoordinates(null)
    setCurrentStep(1)
    setErrors({})

    // Ensure auth context is reset so /register does not bounce away
    logout()

    // Clear persisted progress/token data as requested
    localStorage.clear()

    navigate('/register', { replace: true })
  }

  const fileInputClass = 'block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 file:cursor-pointer border border-gray-200 rounded-xl bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all'
  const textInputClass = 'w-full px-4 py-2.5 text-sm border border-gray-200 bg-white rounded-xl placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all'
  const inputBaseClass = (err?: boolean) =>
    `w-full px-4 py-2.5 text-sm border bg-white rounded-xl placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all ${err ? 'border-red-300 bg-red-50/50' : 'border-gray-200'}`

  const docKeyToDocType: Record<string, string> = {
    businessRegDoc: 'businessReg',
    gstDoc: 'gst',
    addressProofDoc: 'addressProof',
    yogaAllianceDoc: 'yogaAlliance',
  }

  const handleDocFile = (key: keyof typeof docData) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    const validation = validateDocumentFile(file, 'verificationDocument', 0)
    if (!validation.valid) {
      toast.error(validation.error || 'Invalid document')
      return
    }
    const docType = docKeyToDocType[key as string] || 'additional'
    const isImage = file.type.startsWith('image/')
    const previewItem: DocumentPreviewItem = {
      fileKey: '',
      url: '',
      fileName: file.name,
      type: isImage ? 'image' : 'pdf',
      documentType: docType,
      uploading: true,
    }
    setDocData((prev) => ({ ...prev, [key]: previewItem }))
    try {
      const { data } = await profileService.getVerificationDocumentUploadUrl(file.name, file.type, file.size)
      await mediaService.uploadFileToS3(file, data.uploadUrl)
      setDocData((prev) => ({
        ...prev,
        [key]: {
          ...previewItem,
          fileKey: data.fileKey,
          url: data.cloudFrontUrl,
          uploading: false,
        } as DocumentPreviewItem,
      }))
    } catch (err: any) {
      console.error('Document upload failed:', err)
      toast.error(err?.message || 'Failed to upload document. Please try again.')
      setDocData((prev) => ({ ...prev, [key]: null }))
    }
  }

  const handleDocText = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setDocData((prev) => ({ ...prev, [name]: value }))
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    const validation = validateImageFile(file, 'schoolLogo', logoData ? 1 : 0)
    if (!validation.valid) {
      toast.error(validation.error || 'Invalid image')
      return
    }
    setLogoData({ fileKey: '', url: '', uploading: true })
    try {
      const result = await mediaService.uploadFile(file, 'logo', undefined, undefined)
      if (result.success && result.data.fileKey) {
        const url = mediaService.getMediaUrlFromKey(result.data.fileKey)
        setLogoData({ fileKey: result.data.fileKey, url, uploading: false })
      } else {
        throw new Error('Upload failed')
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to upload logo. Please try again.')
      setLogoData(null)
    }
  }

  const handleOtherDocuments = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    const acceptedTypes = [...ACCEPTED_MIME_TYPES.DOCUMENT, 'application/x-pdf'] as readonly string[]
    const valid = Array.from(files).filter((f) => acceptedTypes.includes(f.type) || (f.type === '' && /\.(pdf|jpe?g|png|webp)$/i.test(f.name)))
    e.target.value = ''
    const maxAdditional = MEDIA_SECTIONS.verificationDocumentAdditional.maxDocuments ?? 5
    const slotsLeft = maxAdditional - otherDocuments.length
    if (valid.length === 0) {
      toast.error('Invalid file type. Please upload PDF or images (JPEG, PNG, WebP).')
      return
    }
    const toUpload = valid.slice(0, Math.max(0, slotsLeft))
    if (toUpload.length === 0) {
      toast.error(`Maximum ${maxAdditional} additional documents allowed.`)
      return
    }
    const uploadOne = async (file: File, prev: DocumentPreviewItem[]): Promise<DocumentPreviewItem[]> => {
      const isImage = file.type.startsWith('image/')
      const tempItem: DocumentPreviewItem = { fileKey: '', url: '', fileName: file.name, type: isImage ? 'image' : 'pdf', documentType: 'additional', uploading: true }
      const withTemp = [...prev, tempItem].slice(0, maxAdditional)
      setOtherDocuments(withTemp)
      try {
        const fileTypeForApi = file.type === 'application/x-pdf' ? 'application/pdf' : file.type
        const { data } = await profileService.getVerificationDocumentUploadUrl(file.name, fileTypeForApi, file.size)
        await mediaService.uploadFileToS3(file, data.uploadUrl)
        return withTemp.map((p) => (p === tempItem ? { ...tempItem, fileKey: data.fileKey, url: data.cloudFrontUrl, uploading: false } : p))
      } catch (err: any) {
        toast.error(err?.message || 'Failed to upload document.')
        return withTemp.filter((p) => p !== tempItem)
      }
    }
    let current = [...otherDocuments]
    for (const file of toUpload) {
      const validation = validateDocumentFile(file, 'verificationDocumentAdditional', current.length)
      if (!validation.valid) {
        toast.error(validation.error || 'Invalid document')
        continue
      }
      current = await uploadOne(file, current)
      setOtherDocuments(current)
    }
  }

  const removeOtherDocument = (index: number) => {
    setOtherDocuments((prev) => prev.filter((_, i) => i !== index))
  }

  const getUploadedDocs = (): DocumentPreviewItem[] => {
    const list: DocumentPreviewItem[] = []
    if (docData.businessRegDoc && !docData.businessRegDoc.uploading) list.push(docData.businessRegDoc)
    if (docData.gstDoc && !docData.gstDoc.uploading) list.push(docData.gstDoc)
    if (docData.addressProofDoc && !docData.addressProofDoc.uploading) list.push(docData.addressProofDoc)
    if (docData.yogaAllianceDoc && !docData.yogaAllianceDoc.uploading) list.push(docData.yogaAllianceDoc)
    return [...list, ...otherDocuments.filter((d) => !d.uploading)]
  }

  const getFileKeysAndTypes = (): { fileKeys: string[]; documentTypes: string[] } => {
    const docs = getUploadedDocs()
    return {
      fileKeys: docs.map((d) => d.fileKey),
      documentTypes: docs.map((d) => d.documentType),
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Step 2 - validate documents
    const { fileKeys, documentTypes } = getFileKeysAndTypes()
    if (fileKeys.length === 0) {
      setErrors({ _general: 'Please upload at least one verification document.' })
      return
    }

    const authToken = localStorage.getItem('authToken')
    if (!authToken) {
      toast.error('You are not logged in. Please login and try again.')
      navigate('/login', { replace: true })
      return
    }

    setIsLoading(true)
    setErrors({})

    try {
      const schoolData: any = {
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        description: formData.description.trim(),
        location: formData.location.trim() || formData.city.trim(),
        country: formData.country.trim(),
        city: formData.city.trim(),
      }

      // Add optional fields only if they have values
      if (formData.shortDescription.trim()) {
        schoolData.shortDescription = formData.shortDescription.trim()
      }
      if (formData.address.trim()) {
        schoolData.address = formData.address.trim()
      }
      if (formData.phone.trim()) {
        schoolData.phone = formData.phone.trim()
      }
      if (formData.email.trim()) {
        schoolData.email = formData.email.trim().toLowerCase()
      }
      if (formData.website.trim()) {
        schoolData.website = formData.website.trim()
      }
      if (coordinates) {
        schoolData.coordinates = coordinates
      }
      const validStations = howToReach
        .filter((s) => s.name.trim() && s.options.some((o) => o.mode))
        .map((s) => ({
          name: s.name.trim(),
          options: s.options
            .filter((o) => o.mode)
            .map((o) => ({
              mode: o.mode,
              ...(o.details.trim() ? { details: o.details.trim() } : {}),
            })),
        }))
      if (validStations.length > 0) {
        schoolData.howToReach = { stations: validStations }
      }
      if (logoData?.fileKey) {
        schoolData.images = { logo: logoData.fileKey }
      }

      console.log('Creating school with data:', schoolData)
      const response = await apiService.createSchool(schoolData)
      console.log('School creation response:', response)

      if (response.success) {
        console.log('School created successfully:', response.data)

        // Submit verification document keys (already uploaded to Cloudflare when user selected files)
        if (fileKeys.length > 0) {
          try {
            const meta: Record<string, string> = {}
            if (docData.businessRegNumber.trim()) meta.businessRegNumber = docData.businessRegNumber.trim()
            if (docData.gstNumber.trim()) meta.gstNumber = docData.gstNumber.trim()
            if (docData.yogaAllianceNumber.trim()) meta.yogaAllianceNumber = docData.yogaAllianceNumber.trim()

            await profileService.submitVerificationDocumentsByKeys(fileKeys, documentTypes, Object.keys(meta).length > 0 ? meta : undefined)
            toast.success('School and documents submitted successfully! Your application is pending approval.')
          } catch (docError: any) {
            console.error('Document submit error:', docError)
            toast.success('School created! Document submission failed – you can submit documents later from your profile.')
          }
        } else {
          toast.success('Yoga school created successfully! It is pending approval and will be visible once approved.')
        }
        
        // Refresh schools list in auth context before redirecting
        // This ensures ProtectedRoute will see the new school
        try {
          console.log('Refreshing schools list...')
          await refreshSchools()
          console.log('Schools refreshed after creation')
          
          // Double-check that schools were actually loaded
          // Wait a bit more to ensure state is updated
          await new Promise(resolve => setTimeout(resolve, 500))
        } catch (err) {
          console.error('Error refreshing schools:', err)
          // Force a full page reload if refresh fails
          setTimeout(() => {
            window.location.href = '/dashboard'
          }, 1000)
          return
        }
        
        // Wait a bit, then redirect to dashboard
        // The auth context now has the school, so ProtectedRoute won't redirect back
        setTimeout(() => {
          navigate('/dashboard', { replace: true })
        }, 500)
      } else {
        throw new Error(response.message || 'School creation failed')
      }
    } catch (error: any) {
      console.error('School creation error:', error)
      console.error('Error response:', error.response)
      console.error('Error data:', error.response?.data)
      console.error('Error status:', error.response?.status)
      console.error('Has auth token:', !!localStorage.getItem('authToken'))
      
      const errData = error.response?.data
      const errObj = errData?.error
      let errorMessage = (typeof errObj === 'object' && errObj?.message) || errObj || errData?.message || error.message || 'Failed to create school. Please try again.'
      errorMessage = String(errorMessage)

      // Provide more specific error messages
      if (error.response?.status === 401) {
        errorMessage = 'You are not logged in. Please login and try again.'
        toast.error(errorMessage)
        setTimeout(() => {
          navigate('/login', { replace: true })
        }, 2000)
        return
      }

      if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to create a school. Please contact support.'
      }

      toast.error(errorMessage)

      // Set field-specific errors if available
      if (errData?.errors && typeof errData.errors === 'object') {
        setErrors(errData.errors)
      } else if (errorMessage.includes('slug') || errorMessage.includes('name')) {
        if (errorMessage.includes('slug')) {
          setErrors({ slug: 'This slug is already taken. Please choose a different one.' })
        }
        if (errorMessage.includes('name')) {
          setErrors({ name: 'A school with this name already exists.' })
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const progressPercent = currentStep === 1 ? 0 : 50

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center mb-8"
        >
          <img src="/logo.png" alt="BookMyYogaTTC" className="h-10 sm:h-12 w-auto object-contain mb-6 mx-auto block" />
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
            Create Your Yoga School
          </h1>
          <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
            {currentStep === 1
              ? 'Add your school information to get started'
              : 'Upload verification documents to complete your application'}
          </p>
          {currentStep === 2 && (
            <button
              type="button"
              onClick={() => setShowDocumentsModal(true)}
              className="mt-3 inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              <FileText className="h-4 w-4" />
              What documents do I need?
            </button>
          )}
        </motion.div>

        <DocumentsRequiredModal isOpen={showDocumentsModal} onClose={() => setShowDocumentsModal(false)} />

        {/* Progress bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex flex-col items-center flex-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-300 ${
                  currentStep >= 1 ? 'bg-primary-600 text-white shadow-lg shadow-primary-500/30' : 'bg-gray-200 text-gray-500'
                }`}
              >
                {currentStep > 1 ? <CheckCircle className="h-5 w-5 text-white" /> : '1'}
              </div>
              <span className={`mt-2 text-xs font-medium ${currentStep >= 1 ? 'text-primary-600' : 'text-gray-400'}`}>
                School Details
              </span>
            </div>
            <div className="flex-1 h-1.5 mx-2 sm:mx-4 min-w-[60px]">
              <div className="h-full w-full bg-gray-200 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"
                  initial={{ width: '0%' }}
                  animate={{ width: `${progressPercent}%` }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                />
              </div>
            </div>
            <div className="flex flex-col items-center flex-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-300 ${
                  currentStep >= 2 ? 'bg-primary-600 text-white shadow-lg shadow-primary-500/30' : 'bg-gray-200 text-gray-500'
                }`}
              >
                {currentStep > 2 ? <CheckCircle className="h-5 w-5 text-white" /> : '2'}
              </div>
              <span className={`mt-2 text-xs font-medium ${currentStep >= 2 ? 'text-primary-600' : 'text-gray-400'}`}>
                Documents
              </span>
            </div>
          </div>
          <div className="flex justify-between mt-1 px-1">
            <span className="text-xs text-gray-400">Step {currentStep} of 2</span>
            <span className="text-xs text-gray-400">{progressPercent}% complete</span>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden"
          onSubmit={handleSubmit}
        >
          <div className="p-6 sm:p-8 space-y-6">
          {errors._general && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4">
              <p className="text-sm text-red-800">{errors._general}</p>
            </div>
          )}

          <AnimatePresence mode="wait">
            {currentStep === 1 ? (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                School Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <School className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleNameChange}
                  maxLength={LIMITS.schoolName.max}
                  className={`pl-10 pr-4 ${inputBaseClass(!!errors.name)}`}
                  placeholder="e.g. Serenity Yoga School"
                />
              </div>
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>

            <div>
              <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1.5">
                URL Slug <span className="text-red-500">*</span>
                <span className="text-gray-500 text-xs font-normal ml-1">(auto-generated from name)</span>
              </label>
              <input
                id="slug"
                name="slug"
                type="text"
                required
                  value={formData.slug}
                  onChange={handleChange}
                  maxLength={LIMITS.slug.max}
                  className={`font-mono ${inputBaseClass(!!errors.slug)}`}
                placeholder="e.g. serenity-yoga-school"
              />
              {errors.slug && <p className="mt-1 text-sm text-red-600">{errors.slug}</p>}
              <p className="mt-1 text-xs text-gray-500">
                Used in your school URL: …/schools/{formData.slug || 'your-slug'}
              </p>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1.5">
                Description <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FileText className="absolute left-3.5 top-3.5 h-5 w-5 text-gray-400 pointer-events-none" />
                <textarea
                  id="description"
                  name="description"
                  required
                  rows={4}
                  value={formData.description}
                  onChange={handleChange}
                  maxLength={LIMITS.description.max}
                  className={`pl-10 pr-4 py-2.5 resize-none ${inputBaseClass(!!errors.description)}`}
                  placeholder="Tell students about your school, philosophy, and what makes you unique."
                />
              </div>
              {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
              <p className="mt-1 text-xs text-gray-500">
                {formData.description.length}/700 characters
              </p>
            </div>

            <div>
              <label htmlFor="shortDescription" className="block text-sm font-medium text-gray-700 mb-1.5">
                Short Description <span className="text-gray-500 text-xs font-normal">(Optional)</span>
              </label>
              <textarea
                id="shortDescription"
                name="shortDescription"
                rows={2}
                maxLength={LIMITS.shortDescription.max}
                value={formData.shortDescription}
                onChange={handleChange}
                className={inputBaseClass(!!errors.shortDescription) + ' resize-none'}
                placeholder="Brief tagline or summary (max 200 characters)"
              />
              <p className="mt-1 text-xs text-gray-500">
                {formData.shortDescription.length}/200 characters
              </p>
              {errors.shortDescription && <p className="mt-1 text-sm text-red-600">{errors.shortDescription}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                School Logo <span className="text-gray-500 text-xs font-normal">(Optional)</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">PNG or JPG, max 2MB. Recommended: 200×200px.</p>
              <div className="flex items-start gap-4">
                {logoData ? (
                  <div className="relative">
                    {logoData.uploading ? (
                      <div className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center bg-gray-50">
                        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                      </div>
                    ) : (
                      <>
                        <img src={logoData.url} alt="School logo preview" className="w-24 h-24 object-contain rounded-xl border border-gray-200 bg-white" />
                        <button
                          type="button"
                          onClick={() => setLogoData(null)}
                          className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 text-xs"
                          aria-label="Remove logo"
                        >
                          ×
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full min-h-[96px] rounded-xl border-2 border-dashed border-gray-300 hover:border-primary-400 hover:bg-primary-50/50 cursor-pointer transition-colors">
                    <ImageIcon className="h-8 w-8 text-gray-400" />
                    <span className="text-sm text-gray-600 mt-2">Click to upload logo</span>
                    <input type="file" accept={ACCEPT_STRINGS.IMAGE} onChange={handleLogoUpload} className="hidden" />
                  </label>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1.5">
                Street <span className="text-red-500">*</span>
              </label>
              <input
                id="address"
                name="address"
                type="text"
                required
                maxLength={LIMITS.address.max}
                value={formData.address}
                onChange={handleChange}
                className={inputBaseClass(!!errors.address)}
                placeholder="e.g. 123 Main Street, Tapovan"
              />
              {errors.address && <p className="mt-1 text-sm text-red-600">{errors.address}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1.5">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  id="city"
                  name="city"
                  type="text"
                  required
                  value={formData.city}
                  onChange={handleChange}
                  className={inputBaseClass(!!errors.city)}
                  placeholder="e.g. Rishikesh"
                />
                {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
              </div>

              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Country <span className="text-red-500">*</span>
                </label>
                <CountrySelect
                  id="country"
                  value={formData.country}
                  onChange={(v) => {
                    setFormData(prev => ({ ...prev, country: v }))
                    if (errors.country) setErrors(prev => ({ ...prev, country: '' }))
                  }}
                  className={inputBaseClass(!!errors.country)}
                  placeholder="e.g. India"
                  required
                />
                {errors.country && <p className="mt-1 text-sm text-red-600">{errors.country}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1.5">
                Area / Locality <span className="text-gray-500 text-xs font-normal">(Optional)</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                <input
                  id="location"
                  name="location"
                  type="text"
                  value={formData.location}
                  onChange={handleChange}
                  className={`pl-10 pr-4 ${inputBaseClass(!!errors.location)}`}
                  placeholder="e.g. Tapovan, Near Ram Jhula"
                />
              </div>
              {errors.location && <p className="mt-1 text-sm text-red-600">{errors.location}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Map Location <span className="text-gray-500 text-xs font-normal">(Optional)</span>
              </label>
              {!showMapPicker ? (
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setShowMapPicker(true)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-xl hover:bg-primary-100 transition-colors"
                  >
                    <Map className="h-4 w-4" />
                    {coordinates ? 'Change location on map' : 'Set location on map'}
                  </button>
                  {coordinates && (
                    <p className="text-sm text-gray-600">
                      Location set: {coordinates.lat.toFixed(5)}, {coordinates.lng.toFixed(5)}
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <MapLocationPicker
                    value={coordinates}
                    onChange={setCoordinates}
                    placeholder="Search for your address below or click on the map to set location"
                    initialAddress={[formData.address, formData.location, formData.city, formData.country].filter(Boolean).join(', ')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowMapPicker(false)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Hide map
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Phone <span className="text-gray-500 text-xs font-normal">(Optional)</span>
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className={inputBaseClass(!!errors.phone)}
                  placeholder="e.g. +91 98765 43210"
                />
                {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email <span className="text-gray-500 text-xs font-normal">(Optional)</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={inputBaseClass(!!errors.email)}
                  placeholder="e.g. contact@yourschool.com"
                />
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1.5">
                Website <span className="text-gray-500 text-xs font-normal">(Optional)</span>
              </label>
              <div className="relative">
                <Globe className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                <input
                  id="website"
                  name="website"
                  type="url"
                  value={formData.website}
                  onChange={handleChange}
                  className={`pl-10 pr-4 ${inputBaseClass(!!errors.website)}`}
                  placeholder="https://yourschool.com"
                />
              </div>
              {errors.website && <p className="mt-1 text-sm text-red-600">{errors.website}</p>}
            </div>

            {/* How to Reach - stations with connecting dotted line */}
            <HowToReachForm
              stations={howToReach}
              onChange={setHowToReach}
              inputClass={inputBaseClass(false)}
              schoolName={formData.name}
            />
              </motion.div>
            ) : (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-6"
              >
                <div className="rounded-xl border border-primary-200 bg-primary-50/70 p-4 space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900">What documents to upload</h3>
                  <ul className="text-sm text-gray-700 space-y-1.5 list-disc list-inside">
                    <li>
                      <strong>Minimum to submit:</strong> at least one file below (PDF or image, max 2MB per file).
                    </li>
                    <li>
                      <strong>Recommended:</strong> official <strong>business registration proof</strong> (see first section) <strong>and</strong> address proof. A <strong>GST registration certificate</strong> counts as business registration for many schools—upload it there if that is your main document.
                    </li>
                    <li>
                      <strong>Optional:</strong> separate GST block below only if you want GSTIN on file in addition; Yoga Alliance (if RYS); Additional documents.
                    </li>
                  </ul>
                  <button
                    type="button"
                    onClick={() => setShowDocumentsModal(true)}
                    className="inline-flex items-center gap-2 text-sm font-medium text-primary-700 hover:text-primary-800 underline-offset-2 hover:underline"
                  >
                    <FileText className="h-4 w-4" />
                    Full checklist with examples
                  </button>
                </div>

                {/* Business Registration */}
                <fieldset className="border border-gray-200 rounded-xl p-4 space-y-3">
                  <legend className="text-sm font-semibold text-gray-800 px-1">Business registration <span className="text-red-500">*</span></legend>
                  <p className="text-xs text-gray-600 -mt-1">
                    Official proof that your school/business exists: e.g. incorporation / LLP / society / trust certificate, UDYAM, or <strong>GST registration certificate</strong> (GST is a common form of business registration—if that is your primary proof, upload it here). Enter the number shown on that document (CIN, LLPIN, GSTIN, UDYAM, etc.).
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Registration number on document</label>
                    <input
                      type="text"
                      name="businessRegNumber"
                      value={docData.businessRegNumber}
                      onChange={handleDocText}
                      placeholder="e.g. CIN, LLPIN, GSTIN, or UDYAM number"
                      className={textInputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Upload certificate (PDF or image)</label>
                    <input type="file" accept={ACCEPT_STRINGS.DOCUMENT} onChange={handleDocFile('businessRegDoc')} className={fileInputClass} />
                    {docData.businessRegDoc && (
                      <div className="mt-2">
                        <DocumentPreviewCard item={docData.businessRegDoc} onRemove={() => setDocData((p) => ({ ...p, businessRegDoc: null }))} />
                      </div>
                    )}
                  </div>
                </fieldset>

                {/* GST — optional duplicate path for teams who split entity vs GST docs */}
                <fieldset className="border border-gray-200 rounded-xl p-4 space-y-3">
                  <legend className="text-sm font-semibold text-gray-800 px-1">GST details <span className="text-gray-500 font-normal">(optional)</span></legend>
                  <p className="text-xs text-gray-600 -mt-1">
                    If you <strong>already uploaded your GST certificate</strong> under Business registration, you can <strong>skip this section</strong>. Use it only when you want GSTIN and the GST certificate filed separately (e.g. entity proof above is incorporation, and GST is additional).
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">GST number</label>
                    <input
                      type="text"
                      name="gstNumber"
                      value={docData.gstNumber}
                      onChange={handleDocText}
                      placeholder="e.g. 27AABCU9603R1ZM"
                      className={textInputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Upload GST certificate</label>
                    <input type="file" accept={ACCEPT_STRINGS.DOCUMENT} onChange={handleDocFile('gstDoc')} className={fileInputClass} />
                    {docData.gstDoc && (
                      <div className="mt-2">
                        <DocumentPreviewCard item={docData.gstDoc} onRemove={() => setDocData((p) => ({ ...p, gstDoc: null }))} />
                      </div>
                    )}
                  </div>
                </fieldset>

                {/* Address Proof */}
                <fieldset className="border border-gray-200 rounded-xl p-4 space-y-3">
                  <legend className="text-sm font-semibold text-gray-800 px-1">Address Proof</legend>
                  <p className="text-xs text-gray-600 -mt-1">
                    Document showing the school or business address: recent utility bill, lease/rent agreement, property tax receipt, or municipal certificate. Should align with the address you entered in step 1 where possible.
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Upload document</label>
                    <input type="file" accept={ACCEPT_STRINGS.DOCUMENT} onChange={handleDocFile('addressProofDoc')} className={fileInputClass} />
                    {docData.addressProofDoc && (
                      <div className="mt-2">
                        <DocumentPreviewCard item={docData.addressProofDoc} onRemove={() => setDocData((p) => ({ ...p, addressProofDoc: null }))} />
                      </div>
                    )}
                  </div>
                </fieldset>

                {/* Yoga Alliance */}
                <fieldset className="border border-gray-200 rounded-xl p-4 space-y-3">
                  <legend className="text-sm font-semibold text-gray-800 px-1">Yoga Alliance Certification <span className="text-gray-500 font-normal">(if certified)</span></legend>
                  <p className="text-xs text-gray-600 -mt-1">Only if you are a Registered Yoga School (RYS) or have equivalent certification you want verified. Optional for schools still seeking registration.</p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">RYS registration number</label>
                    <input
                      type="text"
                      name="yogaAllianceNumber"
                      value={docData.yogaAllianceNumber}
                      onChange={handleDocText}
                      placeholder="e.g. RYS-200-12345"
                      className={textInputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Upload certificate</label>
                    <input type="file" accept={ACCEPT_STRINGS.DOCUMENT} onChange={handleDocFile('yogaAllianceDoc')} className={fileInputClass} />
                    {docData.yogaAllianceDoc && (
                      <div className="mt-2">
                        <DocumentPreviewCard item={docData.yogaAllianceDoc} onRemove={() => setDocData((p) => ({ ...p, yogaAllianceDoc: null }))} />
                      </div>
                    )}
                  </div>
                </fieldset>

                {/* Additional Documents */}
                <fieldset className="border border-gray-200 rounded-xl p-4 space-y-3">
                  <legend className="text-sm font-semibold text-gray-800 px-1">Additional Documents <span className="text-gray-500 font-normal">(optional)</span></legend>
                  <p className="text-xs text-gray-500 -mt-1">Insurance, tax certificates, course brochures, etc.</p>
                  <label className="block cursor-pointer">
                    <span className="sr-only">Upload additional documents</span>
                    <input
                      id="additional-docs-input"
                      type="file"
                      accept={ACCEPT_STRINGS.DOCUMENT}
                      multiple
                      onChange={handleOtherDocuments}
                      className={fileInputClass}
                    />
                  </label>
                  {otherDocuments.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {otherDocuments.map((doc, i) => (
                        <DocumentPreviewCard key={i} item={doc} onRemove={() => removeOtherDocument(i)} />
                      ))}
                    </div>
                  )}
                </fieldset>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="rounded-xl bg-primary-50/80 border border-primary-200/50 p-4">
            <p className="text-sm text-gray-700">
              <strong className="text-primary-700">Note:</strong> Your school will be submitted for approval. Once approved by our administrators, it will be visible on the platform.
            </p>
          </div>
          </div>

          <div className="px-6 sm:px-8 py-4 bg-gray-50/80 border-t border-gray-100 flex gap-2">
            {currentStep === 2 && (
              <button
                type="button"
                onClick={handleBackStep}
                disabled={isLoading}
                className="flex items-center justify-center gap-1.5 py-2 px-4 text-sm border border-gray-200 bg-white text-gray-700 font-medium rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 disabled:opacity-50 transition-colors shrink-0"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </button>
            )}
            {currentStep === 1 ? (
              <div className="w-full space-y-2">
                <button
                  type="button"
                  onClick={handleBackToRegistration}
                  className="w-full flex justify-center items-center py-2 px-4 text-sm border border-red-200 bg-white text-red-600 font-medium rounded-xl hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500/20 transition-colors"
                >
                  Back to Registration
                </button>
                <button
                  type="button"
                  onClick={() => validateForm() && handleNextStep()}
                  className="w-full flex justify-center items-center gap-1.5 py-2 px-4 text-sm bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                >
                  Continue
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                type="submit"
                disabled={isLoading || getUploadedDocs().length === 0}
                className="flex-1 flex justify-center items-center gap-1.5 py-2 px-4 text-sm bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 text-white" />
                    Submit for Approval
                  </>
                )}
              </button>
            )}
          </div>
        </motion.form>
      </div>
    </div>
  )
}

export default CreateSchool
